<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useMapGetter } from 'dashboard/composables/store';
import ContactInteractionsAPI from 'dashboard/api/contactInteractions';

import ConversationCard from 'dashboard/components/widgets/conversation/ConversationCard.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  selectedContactId: { type: [String, Number], default: 0 },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const inboxGetter = useMapGetter('inboxes/getInbox');
const inboxesList = useMapGetter('inboxes/getInboxes');
const showInboxName = computed(() => inboxesList.value.length > 1);

// Local, isolated state: one card per contact (their most recent conversation),
// ordered by the conversation's last activity so our replies bump it to the top.
const conversations = ref([]);
const meta = ref({ count: 0, currentPage: 1, totalPages: 1 });
const page = ref(1);
const searchValue = ref('');
const isFetching = ref(false);
const isLoadingMore = ref(false);

const hasMore = computed(() => meta.value.currentPage < meta.value.totalPages);

// Bumped on every fresh (non-append) fetch so a slow, stale response can never
// overwrite the results of a newer search.
let requestToken = 0;

const fetchPage = async (pageNumber, { append = false } = {}) => {
  if (append) {
    isLoadingMore.value = true;
  } else {
    requestToken += 1;
    isFetching.value = true;
  }
  const token = requestToken;
  try {
    const { data } = await ContactInteractionsAPI.get(
      pageNumber,
      searchValue.value
    );
    if (token !== requestToken) return;
    conversations.value = append
      ? [...conversations.value, ...(data.payload || [])]
      : data.payload || [];
    meta.value = {
      count: data.meta?.count || 0,
      currentPage: data.meta?.current_page || 1,
      totalPages: data.meta?.total_pages || 1,
    };
    page.value = pageNumber;
  } catch (error) {
    if (!append && token === requestToken) {
      conversations.value = [];
    }
  } finally {
    if (token === requestToken) {
      isFetching.value = false;
      isLoadingMore.value = false;
    }
  }
};

const loadMore = () => {
  if (!hasMore.value || isFetching.value || isLoadingMore.value) return;
  fetchPage(page.value + 1, { append: true });
};

const onScroll = event => {
  const { scrollTop, scrollHeight, clientHeight } = event.target;
  if (scrollHeight - scrollTop - clientHeight < 160) {
    loadMore();
  }
};

const runSearch = debounce(() => fetchPage(1), 300);

const handleSearchInput = event => {
  searchValue.value = event.target.value;
  runSearch();
};

const contactOf = conversation => conversation.meta?.sender || {};
const isActive = conversation =>
  Number(props.selectedContactId) === contactOf(conversation).id;

const onSelect = conversation => {
  const contact = contactOf(conversation);
  if (contact.id) emit('select', contact);
};

onMounted(() => fetchPage(1));
</script>

<template>
  <div class="flex flex-col h-full border-r border-n-weak bg-n-surface-1">
    <div class="flex-shrink-0 px-4 pt-4 pb-3 border-b border-n-weak">
      <h1 class="mb-3 text-lg font-medium text-n-slate-12">
        {{ t('CONTACT_INTERACTIONS.HEADER.TITLE') }}
      </h1>
      <div class="relative">
        <span
          class="absolute inset-y-0 left-0 flex items-center pl-3 text-n-slate-10 i-lucide-search size-4"
        />
        <input
          :value="searchValue"
          type="text"
          class="w-full py-2 border rounded-lg pl-9 pr-3 text-sm reset-base bg-n-alpha-black1 text-n-slate-12 placeholder:text-n-slate-10 border-n-weak focus:outline-none focus:border-n-brand"
          :placeholder="t('CONTACT_INTERACTIONS.SEARCH_PLACEHOLDER')"
          @input="handleSearchInput"
        />
      </div>
    </div>

    <div
      v-if="isFetching && !conversations.length"
      class="flex items-center justify-center flex-1"
    >
      <Spinner />
    </div>

    <div
      v-else-if="!conversations.length"
      class="flex items-center justify-center flex-1 px-4 text-sm text-center text-n-slate-11"
    >
      {{ t('CONTACT_INTERACTIONS.NO_CONTACTS') }}
    </div>

    <ul v-else class="flex-1 py-1 overflow-y-auto" @scroll="onScroll">
      <ConversationCard
        v-for="conversation in conversations"
        :key="conversation.id"
        :chat="conversation"
        :current-contact="contactOf(conversation)"
        :assignee="conversation.meta?.assignee || {}"
        :inbox="inboxGetter(conversation.inbox_id) || {}"
        :is-active-chat="isActive(conversation)"
        :show-inbox-name="showInboxName"
        @click="onSelect(conversation)"
      />
      <li
        v-if="isLoadingMore"
        class="flex items-center justify-center py-3 text-n-slate-10"
      >
        <Spinner />
      </li>
    </ul>
  </div>
</template>
